import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { useStaffManagement } from '@/hooks/useStaffManagement';
import { useSites } from '@/hooks/useSites';
import { useAuth } from '@/contexts/AuthContext';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { UserPlus, Search, Edit, XCircle, Shield, Copy, RotateCcw, Archive, KeyRound } from 'lucide-react';
import { format } from 'date-fns';
import { ROLE_OPTIONS, isSuperAdmin, isProgramDirector, isManager, ROLES, AppRole, getRoleLabel } from '@/constants/roles';
import { toast } from 'sonner';

import { StaffProfileDialog } from './StaffProfileDialog';

interface StaffManagementSectionProps {
  isDemo?: boolean;
}

export function StaffManagementSection({ isDemo = false }: StaffManagementSectionProps) {
  const { user } = useAuth();
  const [testingPassword, setTestingPassword] = useState('');
  
  const { sites: dbSites } = useSites();
  const { staff: dbStaff, isLoading: dbIsLoading, isCreating, createStaff, updateStaff, deactivateStaff, restoreStaff, resetPassword, resetTestingPasswords } = useStaffManagement();

  const sites = dbSites;
  const staff = dbStaff;
  const isLoading = dbIsLoading;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [staffViewMode, setStaffViewMode] = useState<'active' | 'archived'>('active');
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{ fullName: string; email: string; tempPassword: string } | null>(null);
  const [isRoleTestingOpen, setIsRoleTestingOpen] = useState(false);
  const [isResettingTestingPasswords, setIsResettingTestingPasswords] = useState(false);
  const [testingResetComplete, setTestingResetComplete] = useState(false);

  // Create form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [role, setRole] = useState<AppRole>(ROLES.RESIDENTIAL_AIDE);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);

  // Edit form state
  const [editRole, setEditRole] = useState<AppRole>(ROLES.RESIDENTIAL_AIDE);
  const [editSites, setEditSites] = useState<string[]>([]);

  const isSuperAdminOrDirector = isSuperAdmin(user?.role as AppRole) || isProgramDirector(user?.role as AppRole);
  const isManagerRole = isManager(user?.role as AppRole);
  const canUseRoleTesting = isSuperAdminOrDirector || isManagerRole;
  // Only Manager and Program Director can add staff, not Supervisor
  const canAddStaff = isSuperAdminOrDirector || isManagerRole;

  const availableRoles = ROLE_OPTIONS.filter(r => {
    if (isSuperAdminOrDirector) return true; // Super admins and directors see all roles
    if (isManagerRole) return r.managerAccess; // Managers only see residential_aide and supervisor
    return !r.adminOnly; // Other roles see non-admin roles
  });

  const handleCreate = async () => {
    if (!email || !fullName) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (selectedSites.length === 0) {
      toast.error('Please assign at least one facility');
      return;
    }

    const result = await createStaff({
      email,
      fullName,
      role,
      siteIds: selectedSites,
      phone: phone || undefined,
      position: position || undefined
    });

    if (result.success) {
      setCreatedCredentials({
        fullName,
        email,
        tempPassword: result.data?.tempPassword || 'Password not available'
      });
      setIsCreateOpen(false);
      setEmail('');
      setPassword('');
      setFullName('');
      setPhone('');
      setPosition('');
      setRole(ROLES.RESIDENTIAL_AIDE);
      setSelectedSites([]);
    }
  };

  const handleEdit = (member: any) => {
    setEditingStaff(member);
    setEditRole(member.role || ROLES.STAFF);
    // Handle both formats: array of IDs or array of objects with id property
    const siteIds = member.sites?.map((s: any) => typeof s === 'string' ? s : s.id) || [];
    setEditSites(siteIds);
    setIsEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingStaff) return;
    
    setIsUpdating(true);
    try {
      const result = await updateStaff(editingStaff.id, {
        role: editRole,
        siteIds: editSites
      });
      
      if (result.success) {
        setIsEditOpen(false);
        setEditingStaff(null);
        toast.success('Staff member updated successfully');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeactivate = async (userId: string) => {
    if (!confirm('Are you sure you want to deactivate this staff member?')) return;
    await deactivateStaff(userId);
  };

  const handleStaffClick = (member: any) => {
    // Sites should already be enriched with {id, name} from list-users
    const enrichedStaff = {
      ...member,
      sites: member.sites || (member.site_name ? [{ id: member.site_id, name: member.site_name }] : [])
    };
    setSelectedStaff(enrichedStaff);
    setProfileOpen(true);
  };

  const filteredStaff = staff.filter((s: any) => {
    // Filter by active/archived mode
    if (staffViewMode === 'active' && s.is_active === false) return false;
    if (staffViewMode === 'archived' && s.is_active !== false) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      s.email?.toLowerCase().includes(search) ||
      s.full_name?.toLowerCase().includes(search)
    );
  });

  const roleTestingStaff = staff
    .filter((s: any) => s.is_active !== false && s.email)
    .sort((a: any, b: any) => (a.role || '').localeCompare(b.role || '') || (a.full_name || a.email || '').localeCompare(b.full_name || b.email || ''));

  const handleResetRoleTestingPasswords = async () => {
    if (roleTestingStaff.length === 0) return;
    if (!testingPassword || testingPassword.length < 12) {
      toast.error('Enter a testing password (min 12 characters) before resetting.');
      return;
    }

    setIsResettingTestingPasswords(true);
    const result = await resetTestingPasswords(roleTestingStaff.map((member: any) => member.id), testingPassword);
    setIsResettingTestingPasswords(false);
    if (result.success) setTestingResetComplete(true);
  };

  const copyTestingCredentials = (member: any) => {
    if (!testingPassword) {
      toast.error('Set a testing password first.');
      return;
    }
    navigator.clipboard.writeText(`Email: ${member.email}\nPassword: ${testingPassword}`);
    toast.success('Credentials copied to clipboard');
  };

  const handleRestore = async (userId: string) => {
    if (!confirm('Restore this staff member to active status?')) return;
    await restoreStaff(userId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">Staff Management</h2>
          <p className="text-muted-foreground">
            Manage staff members and permissions
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {canUseRoleTesting && (
            <Dialog open={isRoleTestingOpen} onOpenChange={(open) => { setIsRoleTestingOpen(open); if (!open) setTestingResetComplete(false); }}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <KeyRound className="h-4 w-4 mr-2" />
                  Role Testing Passwords
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl">
                <DialogHeader>
                  <DialogTitle>Role Testing Passwords</DialogTitle>
                  <DialogDescription>
                    Reset active staff-level accounts to the shared testing password, then copy credentials to sign in by role.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                      <Label htmlFor="role-testing-password" className="text-muted-foreground text-xs uppercase tracking-wide">Testing Password</Label>
                      <Input
                        id="role-testing-password"
                        type="text"
                        autoComplete="off"
                        placeholder="Enter a temporary password (min 12 chars)"
                        value={testingPassword}
                        onChange={(e) => setTestingPassword(e.target.value)}
                        className="mt-1 font-mono"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        disabled={!testingPassword}
                        onClick={() => {
                          navigator.clipboard.writeText(testingPassword);
                          toast.success('Testing password copied');
                        }}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Password
                      </Button>
                      <Button
                        onClick={handleResetRoleTestingPasswords}
                        disabled={isResettingTestingPasswords || roleTestingStaff.length === 0}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        {isResettingTestingPasswords ? 'Resetting...' : 'Reset these users'}
                      </Button>
                    </div>
                  </div>

                  {testingResetComplete && (
                    <div className="rounded-md border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
                      Passwords were reset without forcing a password-change screen.
                    </div>
                  )}

                  <div className="max-h-[55vh] overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Facilities</TableHead>
                          <TableHead className="text-right">Copy</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {roleTestingStaff.map((member: any) => (
                          <TableRow key={member.id}>
                            <TableCell className="font-medium">{member.full_name || '-'}</TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{getRoleLabel(member.role as AppRole)}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {member.sites?.length ? member.sites.map((site: any) => (
                                  <Badge key={site.id} variant="secondary" className="text-xs">{site.name}</Badge>
                                )) : <span className="text-muted-foreground">-</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => copyTestingCredentials(member)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {canAddStaff && (
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Staff Member
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Add New Staff Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input 
                      id="fullName"
                      value={fullName} 
                      onChange={(e) => setFullName(e.target.value)} 
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email"
                      type="email" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="role">Role *</Label>
                    <Select value={role} onValueChange={(value) => setRole(value as AppRole)}>
                      <SelectTrigger id="role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.map(r => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Assign to Facilities *</Label>
                    <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto p-3 border rounded-md">
                      {sites.map(site => (
                        <div key={site.id} className="flex items-center space-x-2">
                          <Switch
                            id={`site-${site.id}`}
                            checked={selectedSites.includes(site.id)}
                            onCheckedChange={(checked) => {
                              setSelectedSites(prev =>
                                checked
                                  ? [...prev, site.id]
                                  : prev.filter(id => id !== site.id)
                              );
                            }}
                          />
                          <Label htmlFor={`site-${site.id}`} className="cursor-pointer font-normal">
                            {site.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Button onClick={handleCreate} disabled={isCreating} className="w-full">
                  {isCreating ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <ToggleGroup
          type="single"
          value={staffViewMode}
          onValueChange={(val) => { if (val) setStaffViewMode(val as 'active' | 'archived'); }}
          className="bg-muted/50 p-1 rounded-lg"
        >
          <ToggleGroupItem value="active" className="gap-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm px-3">
            <Shield className="h-4 w-4" />
            Active
          </ToggleGroupItem>
          <ToggleGroupItem value="archived" className="gap-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm px-3">
            <Archive className="h-4 w-4" />
            Archived
          </ToggleGroupItem>
        </ToggleGroup>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{staffViewMode === 'active' ? '​' : 'Archived Staff'}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading staff...</div>
          ) : filteredStaff.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No staff members found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Facilities</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStaff.map((member: any) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <button
                        onClick={() => handleStaffClick(member)}
                        className="font-medium text-primary hover:underline cursor-pointer text-left"
                      >
                        {member.full_name || '-'}
                      </button>
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        <Shield className="h-3 w-3 mr-1" />
                        {member.role?.replace('_', ' ') || member.position || 'Staff'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {member.sites?.map((site: any) => (
                            <Badge key={site.id} variant="secondary" className="text-xs">
                              {site.name}
                            </Badge>
                          ))
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.is_active === false ? 'destructive' : 'default'}>
                        {member.is_active === false ? 'Inactive' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {staffViewMode === 'active' ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="Edit"
                            onClick={() => handleEdit(member)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeactivate(member.id)}
                            title="Deactivate"
                          >
                            <XCircle className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleRestore(member.id)}
                          title="Restore"
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Staff Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Staff Member</DialogTitle>
            <DialogDescription>
              Update role and facility assignments for {editingStaff?.full_name || editingStaff?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4">
              <div>
                <Label htmlFor="editRole">Role</Label>
                <Select value={editRole} onValueChange={(value) => setEditRole(value as AppRole)}>
                  <SelectTrigger id="editRole">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Assigned Facilities</Label>
                <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto p-3 border rounded-md">
                  {sites.map(site => (
                    <div key={site.id} className="flex items-center space-x-2">
                      <Switch
                        id={`edit-site-${site.id}`}
                        checked={editSites.includes(site.id)}
                        onCheckedChange={(checked) => {
                          setEditSites(prev =>
                            checked
                              ? [...prev, site.id]
                              : prev.filter(id => id !== site.id)
                          );
                        }}
                      />
                      <Label htmlFor={`edit-site-${site.id}`} className="cursor-pointer font-normal">
                        {site.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={isUpdating || editSites.length === 0} className="flex-1">
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Staff Profile Dialog */}
      <StaffProfileDialog
        staff={selectedStaff}
        open={profileOpen}
        onOpenChange={setProfileOpen}
      />

      {/* Credentials Dialog */}
      <Dialog open={!!createdCredentials} onOpenChange={(open) => { if (!open) setCreatedCredentials(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Staff Account Created</DialogTitle>
            <DialogDescription>
              Share these credentials with the staff member. They will be prompted to create a new password on first login.
            </DialogDescription>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Name</Label>
                <p className="font-medium">{createdCredentials.fullName}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Email</Label>
                <p className="font-medium">{createdCredentials.email}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Temporary Password</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono">
                    {createdCredentials.tempPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdCredentials.tempPassword);
                      toast.success('Password copied to clipboard');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={() => setCreatedCredentials(null)} className="w-full">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
